import { useState, useEffect } from "react";
import "./KycDocuments.css";
import KycDocumentModel from "../../model/KycDocumentModel";
import KycUpload from "../KycUpload/KycUpload";

const KycDocuments = ({ userId }) => {
    const [documents, setDocuments] = useState({});
    const [uploadModal, setUploadModal] = useState({ show: false, documentType: null });
    const [viewModal, setViewModal] = useState({ show: false, url: null, type: null });

    const documentTypes = [
        { key: 'id_front', label: 'ID Front', icon: 'bi-card-heading', description: 'Front of your ID or Passport' },
        { key: 'id_back', label: 'ID Back', icon: 'bi-card-text', description: 'Back of your ID or Driver License' },
        { key: 'proof_address', label: 'Proof of Address', icon: 'bi-house-door', description: 'Utility bill or bank statement (max 3 months old)' },
        { key: 'selfie', label: 'Selfie with ID', icon: 'bi-person-badge', description: 'Take a selfie holding your ID' }
    ];

    useEffect(() => {
        if (!userId) return;

        const unsubscribe = KycDocumentModel.getUserDocuments(userId, (data) => {
            setDocuments(data);
        });

        return () => unsubscribe();
    }, [userId]);

    const openUploadModal = (documentType) => {
        setUploadModal({ show: true, documentType });
    };

    const closeUploadModal = () => {
        setUploadModal({ show: false, documentType: null });
    };

    const openViewModal = (url, fileType) => {
        setViewModal({ show: true, url, type: fileType });
    };

    const closeViewModal = () => {
        setViewModal({ show: false, url: null, type: null });
    };

    const handleDeleteDocument = async (documentType, fileName) => {
        if (!window.confirm('Are you sure you want to delete this document?')) {
            return;
        }

        try {
            await KycDocumentModel.deleteDocument(userId, documentType, fileName);
            alert('Document deleted successfully');
        } catch (error) {
            console.error('Error deleting document:', error);
            alert('Error deleting document');
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            pending: { icon: 'bi-clock-history', class: 'status-pending', text: 'Pending Review' },
            approved: { icon: 'bi-check-circle-fill', class: 'status-approved', text: 'Approved' },
            rejected: { icon: 'bi-x-circle-fill', class: 'status-rejected', text: 'Rejected' }
        };

        const config = statusConfig[status] || statusConfig.pending;

        return (
            <span className={`status-badge ${config.class}`}>
                <i className={`bi ${config.icon}`}></i>
                {config.text}
            </span>
        );
    };

    const isKycComplete = () => {
        return documentTypes.every(doc => 
            documents[doc.key] && documents[doc.key].status === 'approved'
        );
    };

    return (
        <div className="kyc-documents">
            <div className="kyc-header">
                <div className="kyc-title">
                    <i className="bi bi-shield-check"></i>
                    <h2>KYC Verification</h2>
                </div>
                {isKycComplete() && (
                    <div className="kyc-complete-badge">
                        <i className="bi bi-patch-check-fill"></i>
                        Verified
                    </div>
                )}
            </div>

            <div className="kyc-info">
                <p>
                    <i className="bi bi-info-circle"></i>
                    Complete your KYC verification to unlock all features. All documents will be reviewed within 24-48 hours.
                </p>
            </div>

            <div className="kyc-documents-grid">
                {documentTypes.map((docType) => {
                    const doc = documents[docType.key];
                    const hasDocument = !!doc;

                    return (
                        <div key={docType.key} className="kyc-document-card">
                            <div className="document-icon">
                                <i className={`bi ${docType.icon}`}></i>
                            </div>
                            
                            <div className="document-info">
                                <h3>{docType.label}</h3>
                                <p className="document-description">{docType.description}</p>
                                
                                {hasDocument && (
                                    <>
                                        <div className="document-status">
                                            {getStatusBadge(doc.status)}
                                        </div>
                                        
                                        {doc.reviewNotes && (
                                            <div className="review-notes">
                                                <strong>Review Notes:</strong>
                                                <p>{doc.reviewNotes}</p>
                                            </div>
                                        )}

                                        <div className="document-meta">
                                            <span className="upload-date">
                                                <i className="bi bi-calendar3"></i>
                                                Uploaded: {new Date(doc.uploadDate).toLocaleDateString()}
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="document-actions">
                                {hasDocument ? (
                                    <>
                                        <button 
                                            className="btn-view"
                                            onClick={() => openViewModal(doc.downloadURL, doc.fileType)}
                                        >
                                            <i className="bi bi-eye"></i> View
                                        </button>
                                        {doc.status === 'rejected' && (
                                            <>
                                                <button 
                                                    className="btn-reupload"
                                                    onClick={() => openUploadModal(docType.key)}
                                                >
                                                    <i className="bi bi-arrow-repeat"></i> Re-upload
                                                </button>
                                                <button 
                                                    className="btn-delete"
                                                    onClick={() => handleDeleteDocument(docType.key, doc.fileName)}
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <button 
                                        className="btn-upload"
                                        onClick={() => openUploadModal(docType.key)}
                                    >
                                        <i className="bi bi-upload"></i> Upload
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Modal de carga */}
            {uploadModal.show && (
                <KycUpload
                    userId={userId}
                    documentType={uploadModal.documentType}
                    onClose={closeUploadModal}
                    onSuccess={() => {
                        // Recargar documentos
                    }}
                />
            )}

            {/* Modal de visualización */}
            {viewModal.show && (
                <div className="view-document-modal">
                    <div className="view-overlay" onClick={closeViewModal}></div>
                    <div className="view-content">
                        <div className="view-header">
                            <h3>Document Preview</h3>
                            <button onClick={closeViewModal} className="close-btn">
                                <i className="bi bi-x"></i>
                            </button>
                        </div>
                        <div className="view-body">
                            {viewModal.type === 'application/pdf' ? (
                                <iframe 
                                    src={viewModal.url} 
                                    title="PDF Preview"
                                    className="pdf-viewer"
                                ></iframe>
                            ) : (
                                <img src={viewModal.url} alt="Document" />
                            )}
                        </div>
                        <div className="view-actions">
                            <a 
                                href={viewModal.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="btn-download"
                            >
                                <i className="bi bi-download"></i> Download
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default KycDocuments;
